import "./EntityUpdate.scss";
import React, { useState } from "react";
import {
  Tag,
  Modal,
  Form,
  Button,
  Checkbox,
  message,
  InputNumber,
  Tooltip,
} from "antd";
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import _ from "lodash";
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";

const LOGS_URL = ENVIRONMENT.JOB_LOGS_URL;
const UPDATE_STATUS_REQUEST_TABLE = [
  "material_renders",
  "product_base_assets",
  "product_asset_convertor",
  "product_quick_renders",
  "room_base_assets",
  "room_quick_renders",
];

const MODEL_JOBS = [
  "product_base_assets",
  "product_quick_renders",
  "room_base_assets",
  "room_quick_assets"
]

// Move ProductControlls outside to prevent re-rendering
const ProductControlls = ({ selectedJob, regenerating, setRegenerating, regenerateRequest }) => {
  const [form] = Form.useForm();

  const onFinish = (values) => {
    try {
      setRegenerating(true);
      regenerateRequest(
        selectedJob.request_id,
        selectedJob.entity_id,
        selectedJob.entity_type,
        selectedJob.type,
        values
      );
    } catch (error) {
      message.error("Failed to regenerate job");
    }
  };

  const handleSubmit = () => {
    form.submit();
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: "20px", marginBottom: "20px" }}
        onFinish={onFinish}
        initialValues={{
          timeout_minutes: 200,
          use_pro_optimizer: true,
          use_displacement_switch: true,
        }}
      >
        <Form.Item
          name="timeout_minutes"
          label={
            <Tooltip title="The maximum time allowed for the job to complete">
              <span>Timeout (minutes) <InfoCircleOutlined /></span>
            </Tooltip>
          }
          rules={[
            { required: true, message: "Please input timeout minutes!" },
          ]}
        >
          <InputNumber defaultValue={200} type="number" min={60} />
        </Form.Item>
        {MODEL_JOBS.includes(selectedJob?.type) && (
          <Form.Item
            name="use_pro_optimizer"
            valuePropName="checked"
            label={
              <Tooltip title="Disable optimization if job is getting aborted">
                <span>Use Optimizer <InfoCircleOutlined /></span>
              </Tooltip>
            }
          >
            <Checkbox className="custom-checkbox" />
          </Form.Item>
        )}
        {(selectedJob?.type == "scene_renders") && (
          <Form.Item
            name="use_displacement_switch"
            valuePropName="checked"
            label={
              <Tooltip title="Disable displacement if image is rendering black">
                <span>Use Displacement <InfoCircleOutlined /></span>
              </Tooltip>
            }
          >
            <Checkbox className="custom-checkbox" />
          </Form.Item>
        )}
        <Button
          type="primary"
          icon={regenerating ? <LoadingOutlined /> : <ReloadOutlined />}
          onClick={handleSubmit}
          className="job-regenerate-button"
        >
          <span>Regenerate</span>
        </Button>
      </Form>
    </div>
  );
};

const JobDetails = (props) => {
  const { modalOpen, setModalOpen, selectedJob } = props;
  const [regenerating, setRegenerating] = useState(false);

  const getLogsLink = (job) => {
    let url = LOGS_URL + job?.request_id + '/';
    if (job?.type == "scene_renders") {
      url = url + "render_max_logs.txt";
    } else if (job?.type == "product_base_assets") {
      url = url + "asset_export_max_logs.txt";
    } else if (job?.type == "room_base_assets") {
      url = url + "room_exporter_max_logs.txt"
    } else if (job?.type == "product_asset_convertor") {
      url = url + "asset_converter_max_logs.txt"
    }
    return url;
  };

  const regenerateRequest = (
    request_id,
    entity_id,
    entity_type,
    request_type,
    values
  ) => {
    let request_payload = {
      'request_id': request_id,
      'timeout': values.timeout_minutes,
    }

    if (request_type == 'product_base_assets' && !values.use_pro_optimizer) {
      request_payload['use_pro_optimizer'] = false;
    } else if (request_type == 'scene_renders' && !values.use_displacement_switch) {
      request_payload['displacement_switch'] = false;
    }

    if (UPDATE_STATUS_REQUEST_TABLE.includes(request_type)) {
      let url = "";
      let payload = { model_status: 6 };
      if (["product", "room"].includes(entity_type)) {
        const isProduct = entity_type === "product";
        url = isProduct ? ENVIRONMENT.UPDATE_PRODUCT : ENVIRONMENT.UPDATE_ROOM;
        payload = {
          model_status: 6,
          username: localStorage.getItem("username"),
          [isProduct ? "product_id" : "room_id"]: entity_id,
        };
      } else if (entity_type == "material") {
        url = ENVIRONMENT.UPDATE_MATERIAL;
        payload = {
          status: 6,
          username: localStorage.getItem("username"),
          material_id: entity_id,
        };
      }

      axios.post(url, payload).then((res) => {
        updateAutomationRequest(request_payload);
      });
    } else {
      updateAutomationRequest(request_payload);
    }
  };

  const updateAutomationRequest = (request_payload) => {
    axios.post(ENVIRONMENT.AUTOMATION_REQUEST_REGEN, request_payload)
      .then(res => {
        setRegenerating(false);
        message.info('Request regenerate has been submitted successfully.');
        window.location.reload();
      })
  }

  return (
    <>
      {modalOpen && (
        <Modal
          footer={null}
          title="Job Details"
          className="failed-and-aborted-jobs-modal"
          visible={modalOpen}
          onCancel={() => setModalOpen(false)}
        >
          <div className="failed-heading-container">
            <span className="heading-text f-13">Request ID: {selectedJob?.request_id}</span>
            <Tag color={
              selectedJob?.status === 'failed' ? 'red' :
                selectedJob?.status === 'aborted' ? 'orange' :
                  selectedJob?.status === 'completed_successfully' ? 'green' :
                    'default'
            }>{selectedJob?.status}</Tag>
          </div>
          <div className="failed-heading-container">
            <span className="heading-text f-13">Request Type: {selectedJob?.type}</span>
          </div>
          <div className="job-actions">
            {selectedJob?.status == "failed" && <Tag
              className="logs-container"
              color="blue"
              onClick={() => window.open(getLogsLink(selectedJob), "_blank")}
            >
              <DownloadOutlined />
              <span>Download error logs for job</span>
            </Tag>}
            {selectedJob?.error_message && selectedJob.error_message.length > 0 && (
              <div>
                <Tag
                  color="error"
                  className="error-message-tag"
                >
                  {selectedJob.error_message}
                </Tag>
              </div>
            )}
          </div>
          <div className="regenerate-job-container">
            <span className="regenerate-job-header heading-text">Regenerate Job</span>
            <ProductControlls 
              key={selectedJob?.request_id} // Add key to prevent form reset
              selectedJob={selectedJob}
              regenerating={regenerating}
              setRegenerating={setRegenerating}
              regenerateRequest={regenerateRequest}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default JobDetails;
